import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChatLauncher } from "@/components/chat/ChatLauncher";
import { CalendarLauncher } from "@/components/calendar/CalendarLauncher";
import { MiraLauncher } from "@/components/mira/MiraLauncher"; // F-11 — see the mount note below
import { requestCalendarOpen } from "@/features/calendar/calendarBus";
import { requestChatOpen } from "@/features/chat/chatBus";
import { BookOpen, CalendarDays, Home, KeyRound, LogOut, MessageSquare, Rocket, Settings, Target, TrendingUp, Users } from "lucide-react";
import { Button } from "@/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { getRoleShellConfig } from "@/lib/roleRouter";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
// DUAL-ROLE-1-UI — the switcher is ONE component shared by both shells; the menus stay separate.
import { WorkspaceSwitchItem } from "@/components/WorkspaceSwitchItem";
import { BrandWordmark } from "@/components/BrandWordmark";
import { useGettingStartedNavItem } from "@/components/admin/useGettingStartedNavItem";

/**
 * ADMIN-A1 / A1b / ADMIN-UX-1b — the principal's shell (school_admin only). R-A1: a DARK RAIL is
 * admin-only chrome.
 *
 * ADMIN-UX-1b REPLACED THE COLLAPSED RAIL. A1b collapsed it to w-16 and expanded to w-60 on hover
 * or focus, which meant that at rest every place was an unlabelled glyph with its name in a `title`
 * — a tooltip is not a label for touch or for a screen reader's scan, and it forced two workarounds
 * of its own (the wordmark and the section labels both had to be hidden and faded in, because
 * `overflow-hidden` clipped them mid-word). The rail is now a PERSISTENT 88px with labels always
 * visible, built to prototype-spec.md B3. It is still a fixed overlay over a fixed-width spacer, so
 * the page never reflows.
 *
 * The active place is GEOMETRY plus a token — an inset ring and the accent on the icon — never
 * colour alone. (The prototype's own active state is fill-only; that could not be the model.)
 * R-A2: purple is Mira-only, nowhere here. The bottom block is an avatar → menu (Language · Logout)
 * reusing the teacher avatar-menu pattern.
 *
 * RTL: the rail sits on the logical `start` (right in Hebrew) and, anchored at `start-0`, its width
 * grows toward `end` — leftward in RTL — for free. Logical tokens only (no physical margins/paddings).
 */
const ICON_BY_ROUTE = {
  "/admin": Home,
  "/admin/growth": TrendingUp,
  // W39 — the admin's own goals. A target, not a trend: there is no trend to show until MA ships
  // measures, and reusing Growth's icon would make two adjacent rail items indistinguishable.
  "/admin/my-leadership": Target,
  "/admin/team": Users,
  "/admin/library": BookOpen,
  "/admin/setup": Settings,
  "/admin/access": KeyRound,
  "/onboarding": Rocket,
};

/**
 * ADMIN-STORY-3 Wave B (item 3) — the rail's two sections.
 *
 * GROUPING ONLY. Every route, and the ruled ORDER within each group, is exactly what it was; this
 * adds two labels and the whitespace between them. Six places in one undifferentiated column made
 * the daily work (Home, Growth, Team, Library) and the occasional work (Setup, Access) look like
 * the same kind of thing.
 *
 * Membership is by ROUTE, not by index, so a reordering elsewhere cannot silently re-home a place.
 * An item matching no group still renders (see `ungrouped` below) — a nav entry must never vanish
 * because nobody remembered to assign it.
 */
const NAV_GROUPS = [
  // W39 adds /admin/my-leadership to the school group, immediately after Growth. The group's
  // `routes` order is what the rail renders, so this list and roleRouter's adminNav must agree —
  // a route present in adminNav but missing here would fall into `ungrouped` below and render
  // detached from both sections rather than disappearing, which is the net working as intended.
  { key: "school", label: "adminNav.groupSchool", routes: ["/admin", "/admin/growth", "/admin/my-leadership", "/admin/team", "/admin/reports", "/admin/library"] },
  { key: "configuration", label: "adminNav.groupConfiguration", routes: ["/admin/setup", "/admin/schedule", "/admin/access"] },
];

// NAV-SWEEP-1b (N10) — the rail asked for `roles.${role}` with the ROLE CONSTANT ("school_admin"),
// but the locale bundle keys this namespace in camelCase ("roles.schoolAdmin"). The lookup missed on
// every admin, and the defaultValue "" swallowed it silently: the line under her name in the rail
// rendered EMPTY. This maps the constant to the key the bundle actually ships.
const ROLE_LABEL_KEY = {
  teacher: "roles.teacher",
  school_admin: "roles.schoolAdmin",
  training_admin: "roles.trainingAdmin",
  super_admin: "roles.superAdmin",
};

// Bottom block — avatar circle → menu. Reuses the TeacherAvatarMenu pattern (aria-haspopup=menu,
// role=menu, click-away backdrop + Escape). testids admin-menu-logout / lang live inside the menu.
//
// NAV-SWEEP-1b (N2): the "Profile" item is GONE. Reusing the teacher avatar-menu pattern carried its
// /my-profile target across with it — a TEACHER-ONLY route (ProtectedRoute allowedTenantRoles=
// ["teacher"]) sitting in permanent admin chrome, on every admin page. An admin clicking it was
// bounced straight back to /admin with no explanation: a door she could see and could not open, the
// exact class this sweep exists to close. There is no admin personal-profile surface to repoint it
// at (/admin/setup is the SCHOOL's setup, not hers), so the door is removed rather than faked.
// Language + Logout — the items that work for this role — remain.
//
// SIDEBAR-USER-1 — two defects, measured in a real browser before this change:
//   1. THE CHIP put a 36px avatar AND a name/role column into a 72px button (88px rail − padding).
//      The column was a few pixels wide, so the name rendered as "R" and the role as "S." — a
//      clipped label, which the canon forbids. At rail width the chip is now the AVATAR ONLY; the
//      full name is its accessible name (aria-label) — NOT a title: the rail's labels-are-not-
//      tooltips rule covers the chip too (Ruling 1) — and the name, email, role and
//      organisation move into the menu's header, one tap away. The rail has no expanded form
//      (prototype-spec B3), so there is no state in which the name belongs on the chip.
//   2. THE MENU was never clipped by the rail — the rail's overflow is visible. It was rendered
//      INSIDE the 88px rail and anchored at its `end`, so its 208px grew AWAY from the page: off the
//      left edge of the viewport in LTR, off the right in RTL. It is now PORTALLED to document.body
//      and placed beside the rail with LOGICAL insets — insetInlineStart just past the rail's inline
//      edge, insetBlockEnd level with the chip — so it opens upward and outward: rightward in LTR,
//      leftward in RTL, from one set of numbers.
const MENU_GAP_PX = 8;

function AdminUserMenu({ user, logout, role, t }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const chipRef = useRef(null);
  const menuRef = useRef(null);
  const chipId = useId();
  const name = String(user?.name || "").trim();
  const email = String(user?.email || "").trim();
  const label = name || email || t("adminMenu.profile");
  const initial = String(name || email || "?").charAt(0).toUpperCase() || "?";
  const roleLabel = ROLE_LABEL_KEY[role] ? t(ROLE_LABEL_KEY[role]) : "";
  // Same fallback the admin viewer banner uses: the organisation, else the school. Absent → no line.
  const org = String(user?.organization_name || user?.school_name || "").trim();

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    setPos(null);
    if (returnFocus && chipRef.current) chipRef.current.focus();
  }, []);

  // Anchor to the RAIL's inline edge (not the chip's), so the menu sits beside the rail and never
  // over it. Direction is read from the chip's computed style, so it follows the document's `dir`.
  const place = useCallback(() => {
    const chip = chipRef.current;
    if (!chip) return;
    const rail = chip.closest("aside") || chip;
    const railBox = rail.getBoundingClientRect();
    const chipBox = chip.getBoundingClientRect();
    const rtl = window.getComputedStyle(chip).direction === "rtl";
    setPos({
      start: (rtl ? window.innerWidth - railBox.left : railBox.right) + MENU_GAP_PX,
      end: Math.max(MENU_GAP_PX, window.innerHeight - chipBox.bottom),
    });
  }, []);

  // Re-placed on resize and on a language switch (which can flip the direction while it is open).
  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open, place, i18n.language]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  // The items keyboard focus moves through: the language control and every menuitem, in order.
  const items = () => Array.from(menuRef.current?.querySelectorAll('select, [role="menuitem"]') || []);

  // Opening moves focus to the first item (the menu-button pattern) — once the portal has a
  // position, i.e. once the menu is actually in the DOM.
  const ready = Boolean(open && pos);
  useEffect(() => {
    if (ready) items()[0]?.focus();
  }, [ready]);

  const onMenuKeyDown = (e) => {
    const list = items();
    if (!list.length) return;
    const at = list.indexOf(document.activeElement);
    let next = null;
    if (e.key === "ArrowDown" && !e.altKey) next = list[(at + 1) % list.length];
    else if (e.key === "ArrowUp") next = list[(at - 1 + list.length) % list.length];
    else if (e.key === "Home") next = list[0];
    else if (e.key === "End") next = list[list.length - 1];
    else if (e.key === "Tab") { e.preventDefault(); close(); return; }
    // Plain arrows move between items even on the language select: in a menu, ArrowDown on a closed
    // select would otherwise CHANGE the language. Alt+ArrowDown is the select's own "open" key, so it
    // is left to the select (LANG-SELECT-1: this handler used to cancel it too, leaving no keyboard
    // route to change language here); the open list's own arrows and Enter then pick an option.
    if (next) { e.preventDefault(); next.focus(); }
  };

  return (
    <div className="border-t border-card/10 p-2">
      <button
        ref={chipRef}
        id={chipId}
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) { e.preventDefault(); setOpen(true); }
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        data-testid="admin-user-button"
        className="flex min-h-[44px] w-full items-center justify-center rounded-lg py-1.5 transition-colors hover:bg-card/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-admin-accent/70"
      >
        <span
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-card/10 text-sm font-semibold text-card ring-1 ring-inset ring-card/15"
          aria-hidden="true"
        >
          {initial}
        </span>
      </button>
      {ready
        ? createPortal(
          <>
            <button
              type="button"
              aria-hidden="true"
              tabIndex={-1}
              onClick={() => close()}
              data-testid="admin-menu-backdrop"
              className="fixed inset-0 z-40 cursor-default"
            />
            <div
              ref={menuRef}
              role="menu"
              aria-labelledby={chipId}
              onKeyDown={onMenuKeyDown}
              data-testid="admin-user-menu"
              style={{ insetInlineStart: `${pos.start}px`, insetBlockEnd: `${pos.end}px` }}
              className="fixed z-50 w-max min-w-60 max-w-sm rounded-brand border border-border bg-card p-1 text-start text-ink shadow-elevated"
            >
              {/* Identity header. <bdi> keeps a Latin name, email or organisation in its own order
                  inside Hebrew text without flipping the line's alignment. */}
              <div role="none" className="border-b border-border px-3 pb-2.5 pt-2" data-testid="admin-menu-identity">
                <p className="truncate text-sm font-semibold text-ink" data-testid="admin-menu-name"><bdi>{name || email}</bdi></p>
                {name && email ? (
                  <p className="truncate text-xs text-ink-soft" data-testid="admin-menu-email"><bdi>{email}</bdi></p>
                ) : null}
                {roleLabel ? (
                  <p className="mt-1 truncate text-xs text-muted" data-testid="admin-user-role">{roleLabel}</p>
                ) : null}
                {org ? (
                  <p className="truncate text-xs text-muted" data-testid="admin-menu-org"><bdi>{org}</bdi></p>
                ) : null}
              </div>
              <div role="none" className="flex min-h-[44px] items-center px-2 pt-1" data-testid="admin-menu-language">
                <LanguageSwitcher compact />
              </div>
              {/* DUAL-ROLE-1-UI — renders ONLY when /api/me says this account may use both
                  workspaces. A plain admin gets nothing here: not a disabled item, not a tooltip.
                  It sits inside the same menu element, so the roving order at `items()` picks it
                  up without a second list to keep in step. */}
              <WorkspaceSwitchItem
                testId="admin-menu-workspace-switch"
                onDone={() => close(false)}
                className="text-ink-soft hover:bg-canvas focus-visible:bg-canvas focus-visible:outline-none"
              />
              {/* F81 (C-177 §4, P0) — Messages, the row the admin menu did not have. The teacher
                  shell has carried it since the chat FAB was retired (LayoutShell: the
                  `chat.openStaffMessages` row); the admin menu offered only Language and Logout,
                  so an administrator had no door to staff messages at all.
                  SAME STRING and SAME BUS as the teacher row on purpose, so one door cannot
                  acquire two vocabularies or two behaviours: it opens in place and never
                  navigates. It sits inside the menu element, so `items()`'s roving focus order
                  picks it up without a second list to keep in step. */}
              <button
                type="button"
                role="menuitem"
                onClick={() => { close(false); requestChatOpen(); }}
                data-testid="admin-menu-chat"
                className="flex min-h-[44px] w-full items-center gap-2 rounded-brand px-3 text-start text-sm text-ink-soft transition-colors hover:bg-canvas focus-visible:bg-canvas focus-visible:outline-none"
              >
                <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
                {t("chat.openStaffMessages")}
              </button>
              {/* PLACED AFTER MESSAGES, NOT BEFORE IT, AND THAT IS THE WHOLE REASON.
                  First draft put this row first and the floor went RED in two neighbouring
                  suites: AdminShell.test.js:263 asserts focus lands on the FIRST item, and
                  AdminShellLanguage.test.js:94 asserts ArrowDown from the language select
                  lands on the item that FOLLOWS it. Both were position assertions, and that
                  second file had ALREADY been re-aimed once for F81. The ordering here is
                  arbitrary, so churning two other suites' assertions to suit my arbitrary
                  preference is the wrong trade: the row moves instead. Order is now
                  Messages, Calendar, Logout. */}
              {/* P2 #3 (C-209 §4, ADMIN-WS OUT-32) — Calendar, the door the admin did not have.
                  MEASURED with a working control before it was built: as a TEACHER the avatar
                  button is found, the row is present and pressing it OPENS the overlay; as a
                  school_admin the avatar button is not found at all. LayoutShell.js:181 is
                  `{isTeacher ? <TeacherTopNav/>}` and TeacherTopNav renders the avatar menu that
                  carries the only Calendar row in the tree (LayoutShell.js:521) — so she had no
                  Calendar door on any surface, while <CalendarLauncher/> was already mounted on
                  the LayoutShell routes she lands in.
                  THE EXACT INVERSE OF F81b: there the row existed with no panel behind it; here
                  the panel existed with no row in front of it. Same class, opposite half.
                  SAME KEY as the teacher row (`calendar.title`) on purpose — no new client copy,
                  which C-212 routes to the owner, and one door cannot acquire two vocabularies. */}
              <button
                type="button"
                role="menuitem"
                onClick={() => { close(false); requestCalendarOpen(); }}
                data-testid="admin-menu-calendar"
                className="flex min-h-[44px] w-full items-center gap-2 rounded-brand px-3 text-start text-sm text-ink-soft transition-colors hover:bg-canvas focus-visible:bg-canvas focus-visible:outline-none"
              >
                <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                {t("calendar.title")}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => { close(false); logout(); }}
                data-testid="admin-menu-logout"
                className="flex min-h-[44px] w-full items-center gap-2 rounded-brand px-3 text-start text-sm text-ink-soft transition-colors hover:bg-canvas focus-visible:bg-canvas focus-visible:outline-none"
              >
                <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
                {t("adminMenu.logout")}
              </button>
            </div>
          </>,
          document.body
        )
        : null}
    </div>
  );
}

export function AdminShell({ children }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const shell = getRoleShellConfig(user);
  // ADMIN-B2-ADOPT-1 — Getting Started is a CONDITIONAL seventh place: present while setup is
  // incomplete, gone once it is done, so the rail returns to its six ruled places rather than
  // carrying a permanently-finished checklist. Once gone the surface is NOT lost — School Setup
  // keeps a quiet link to it, so it can never become unreachable again (the wound this wave closed).
  //
  // DOCTRINE NOTE, stated because it is a deviation: this shell has read NOTHING but identity until
  // now. It still reads no activity and no counts — the hook returns a nav item or null, and the
  // rail's "no digits" law is unchanged. Navigation state is not a readout.
  const gettingStarted = useGettingStartedNavItem();
  // ORDER (ADMIN-STORY-3 Wave B pre-ruling): Getting Started renders ABOVE the ruled places, not
  // after them — a first-run surface sits BEFORE the groups, so that when Wave B introduces the
  // SCHOOL / CONFIGURATION grouping it can absorb this entry as an ungrouped leader rather than
  // having to move it. Appending it last would have looked fine today and fought Wave B tomorrow.
  const nav = [...(gettingStarted ? [gettingStarted] : []), ...(shell.navItems || [])];

  // ADMIN-STORY-3 Wave B (item 3) — membership resolved once. `ungrouped` is the safety net: a
  // seventh ruled place added without a group entry lands here and still renders, rather than
  // disappearing from the rail because a constant above was not updated.
  const { groups, ungrouped } = React.useMemo(() => {
    const items = shell.navItems || [];
    const built = NAV_GROUPS
      .map((group) => ({ ...group, items: group.routes.map((r) => items.find((i) => i.to === r)).filter(Boolean) }))
      .filter((group) => group.items.length);
    const claimed = new Set(built.flatMap((group) => group.items.map((i) => i.to)));
    return { groups: built, ungrouped: items.filter((i) => !claimed.has(i.to)) };
  }, [shell.navItems]);

  /**
   * ADMIN-UX-1b — one item anatomy, both breakpoints: icon ABOVE label, centred, label ALWAYS
   * VISIBLE. Built to prototype-spec.md B3 (item 68 × 56 single-line / 68 × 68 two-line, radius
   * 12px, padding 10px 4px, label 10.5px/600).
   *
   * LABELS ARE NOT TOOLTIPS. The rail previously carried them in `title`, which is an
   * accessibility floor — a tooltip is not a label for touch or for a screen reader's scan. The
   * prototype shows labels anyway, so making them visible is a fidelity GAIN, not a divergence.
   *
   * WRAPPING IS MEASURED, not hoped: at 68px/10.5px no label exceeds TWO lines in either script,
   * and Hebrew wraps at exactly the same rate as English (3 of 7). The spec's two-height model
   * (56 / 68) therefore holds Hebrew-first, which was the thing worth checking before committing
   * to an 88px rail on a Latin-only prototype.
   *
   * THE AXIS IS THE VARIANT'S, not this call site's. UX-1b reported that `cv-btn-nav` encoded a
   * horizontal assumption (`justify-start`) and that this rail was overriding it back to a column;
   * Goal 0 gate 3 closed that by splitting the variant. `nav-stacked` now owns flex-col, the
   * centring, the 4px/10px box and the 4px icon-to-label gap, so nothing below contradicts what it
   * asked for. What remains here is the RAIL's own business: spec geometry and the dark palette.
   *
   * ACTIVE STATE IS GEOMETRY + a token, never colour alone: an inset ring plus the accent on the
   * icon. The prototype's own active state is fill-only (spec B3), which cannot be the model here.
   */
  const NavItems = ({ items, onNavigate, itemClassName = "" }) => (
    <>
      {items.map((item) => {
        const Icon = ICON_BY_ROUTE[item.to] || Home;
        return (
          <Button asChild variant="nav-stacked" key={item.to}>
          <NavLink
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`admin-nav-${item.to.replace(/\//g, "-").replace(/^-/, "")}`}
            className={({ isActive }) =>
              // `rounded-[12px]` IS still an override of cv-btn's `rounded-brand` (14px), and it
              // is left standing deliberately rather than folded into the variant: 12px is
              // prototype-spec B3's measured value and the radius canon has no 12px tier
              // (--cv-radius-sm/md/lg = 10/14/18). Writing an off-canon radius into the design
              // system would be a token decision taken by a call site. It is the open radius
              // proposal, named here so it stays visible instead of quietly becoming canon.
              // `min-h-[56px]` is not an override: cv-btn's 44px is a FLOOR, and 56 raises it.
              `min-h-[56px] rounded-[12px] ${itemClassName} ${
                isActive
                  ? "bg-card/10 text-card ring-1 ring-inset ring-admin-accent/70"
                  : "text-card/70 hover:bg-card/5 hover:text-card"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center ${isActive ? "text-admin-accent" : ""}`}>
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                {/* 10.5px is the spec's measured value. The canon has no nav type tier — D2
                    proposed one (11/600) and OR-9 is unruled — so this carries the measured
                    figure until that is ruled, rather than borrowing `eyebrow`, whose 0.14em
                    tracking is wrong for a label. */}
                <span className="text-[10.5px] font-semibold leading-[1.15]">{t(item.label)}</span>
              </>
            )}
          </NavLink>
          </Button>
        );
      })}
    </>
  );

  /**
   * `grouped` is the DESKTOP rail only. The mobile strip is a horizontally scrolling row, where a
   * section label would sit beside the links rather than above them and read as another destination.
   * Same items, same order, both surfaces — only the labels are desktop chrome.
   */
  const RailLinks = ({ onNavigate, grouped = false }) => {
    // Narrow width: the items share the bar evenly (`flex-1`) so NOTHING is dropped to fit and
    // nothing scrolls out of reach. Desktop: the spec's fixed 68px item inside the 88px rail.
    if (!grouped) return <NavItems items={nav} onNavigate={onNavigate} itemClassName="flex-1 min-w-0" />;
    return (
      <>
        {/* ADOPT-1 put Getting Started above the ruled places precisely so this grouping could adopt
            it as an UNGROUPED LEADER rather than move it. A first-run surface is not school
            configuration and is not a daily place; it is the thing you do before either. */}
        {gettingStarted ? <NavItems items={[gettingStarted]} onNavigate={onNavigate} itemClassName="w-[68px]" /> : null}
        {groups.map((group) => (
          <div
            key={group.key}
            role="group"
            aria-label={t(group.label)}
            className="mt-2 flex flex-col items-center gap-1 first:mt-0"
            data-testid={`admin-rail-group-${group.key}`}
          >
            {/* The rail collapses to w-16 with `overflow-hidden`, which is what clipped the wordmark
                mid-word (ADMIN-STORY R3). A section label is the same hazard, so it takes the same
                cure: hidden at rest, faded in on the SAME hover/focus trigger the width uses. The
                grouping itself is never lost to a screen reader — `aria-label` on the group carries
                it at every width, which is why the visible copy is aria-hidden rather than doubled. */}
            {/* ADMIN-UX-1b — the rail is now a FIXED 88px with no expanded form, and a text section
                label does not fit that width in either script ("Configuration" / "הגדרות בית הספר").
                The prototype has no section labels at all. So the label becomes a RULE: the grouping
                stays visible as geometry, and `aria-label` on the group carries the name at every
                width exactly as it did before — the visible text was already aria-hidden, so nothing
                is lost to a screen reader. Substituting a divider for text is a rung-2 move: same
                information, canon material, inside the measured geometry. */}
            <span
              aria-hidden="true"
              data-testid={`admin-rail-group-label-${group.key}`}
              className="mx-auto my-1 h-px w-8 bg-card/15"
            />
            <NavItems items={group.items} onNavigate={onNavigate} itemClassName="w-[68px]" />
          </div>
        ))}
        {ungrouped.length ? <NavItems items={ungrouped} onNavigate={onNavigate} itemClassName="w-[68px]" /> : null}
      </>
    );
  };

  return (
    <div className="flex min-h-screen bg-canvas text-ink">
      {/* Desktop: the collapsed rail is a FIXED overlay; this spacer reserves its collapsed width so the
          page never reflows when the rail expands over the content. */}
      <div className="hidden w-[88px] shrink-0 md:block" aria-hidden="true" data-testid="admin-rail-spacer" />
      {/* ADMIN-UX-1b — a persistent 88px rail (prototype-spec.md B3), NOT a collapsed one that
          expands on hover. The collapse existed to buy horizontal space and cost the labels: at
          rest every place was an unlabelled glyph with its name in a `title`, which is a tooltip,
          not a label. The spec's rail has no collapsed form at all, and 88px is cheaper than the
          w-60 the old rail expanded to anyway. Removing the hover/focus width transition also
          removes the wordmark- and section-label clipping the old rail had to work around. */}
      <aside
        className="fixed inset-y-0 start-0 z-30 hidden h-screen w-[88px] flex-col bg-admin-rail md:flex"
        data-testid="admin-rail"
      >
        {/* ADMIN-STORY (R3) — the wordmark is TEXT ("Cognivio"), and the rail collapses to w-16 with
            `overflow-hidden`, so at rest it was clipped MID-WORD. A truncated mark is the one thing
            the canon forbids, and a clipped brand reads as a rendering fault rather than a design.
            So the mark is HIDDEN when collapsed and fades in on the same hover/focus trigger the
            width animation already uses — never a partial mark, and the rail still collapses.
            aria-hidden + tabIndex=-1 travel with the opacity so a screen reader and the tab order
            do not reach a mark that is not visually there. */}
        {/* Always visible now. The old rail hid the mark at rest because w-16 + overflow-hidden
            clipped it MID-WORD; at a fixed 88px "Cognivio" fits at `sm`, so the cure the clipping
            forced (hide it, fade it in on hover) is no longer needed. */}
        <div className="flex justify-center px-2 pb-4 pt-5" data-testid="admin-rail-wordmark">
          <BrandWordmark to="/admin" size="sm" variant="onDark" />
        </div>
        <nav className="flex flex-1 flex-col items-center gap-1 px-2" data-testid="admin-rail-nav">
          <RailLinks grouped />
        </nav>
        <AdminUserMenu user={user} logout={logout} role={shell.role} t={t} />
      </aside>

      {/* Mobile: a dark top strip with a horizontally scrollable rail (unchanged from A1). */}
      <div className="flex min-h-screen flex-1 flex-col">
        {/* Narrow: identity stays at the top; NAVIGATION MOVES TO A BOTTOM BAR. The old strip was a
            horizontally scrolling row, which drops items out of reach in practice — the same
            failure as dropping them outright, only harder to notice. The bar gives every place an
            equal share of the width (`flex-1`) so the order and the reachability are identical to
            the rail's, and the item anatomy is the same one, so there is a single thing to learn. */}
        <div className="bg-admin-rail md:hidden" data-testid="admin-rail-mobile">
          <div className="flex items-center justify-between px-4 py-3">
            <BrandWordmark to="/admin" size="md" variant="onDark" />
            {/* C-205 item 2: at 390px the desktop rail's AdminUserMenu is hidden, so the header carried
                only Logout — the Language switch was missing. Surface both here (the switch is a
                self-contained white select, readable on the dark strip). The full avatar menu is AW-8. */}
            <div className="flex items-center gap-2" data-testid="admin-menu-mobile">
              <div data-testid="admin-menu-language-mobile"><LanguageSwitcher compact /></div>
              <button type="button" onClick={logout} className="min-h-[44px] px-2 text-xs font-semibold text-card/70" data-testid="admin-menu-logout-mobile">
                {t("adminMenu.logout")}
              </button>
            </div>
          </div>
        </div>

        <main className="mx-auto w-full max-w-page flex-1 px-4 py-6 pb-28 sm:px-6 md:pb-6 lg:px-8" data-testid="admin-main">
          {children}
        </main>

        {/* `inset-x-0 bottom-0` are physical only in the sense that "bottom" has no logical
            counterpart for a horizontal bar; the INLINE axis uses inset-x, which mirrors. Item
            order follows the document, so RTL reverses it for free. */}
        <nav
          className="fixed inset-x-0 bottom-0 z-30 flex gap-1 border-t border-card/10 bg-admin-rail px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 md:hidden"
          data-testid="admin-rail-bottom"
        >
          <RailLinks />
        </nav>

        {/* F81 (C-177 §4, P0) — THE PANEL, mounted here because the ROW in the menu above would
            otherwise be a dead control. `requestChatOpen()` is a bus request, and the bus has no
            listener unless ChatLauncher is in the tree: until now it was mounted only in
            LayoutShell (the teacher shell), so an admin pressing Messages would have fired into
            nothing. A door with no room behind it is the F65 defect wearing different clothes.
            ChatLauncher renders no floating button (FabConsolidation) and keeps its own
            `if (!myId) return null` gate, so this adds a panel and not a FAB.
            The chat backend is role-agnostic by construction, which is why this is a frontend-only
            gap: `app/chat/routes.py:80` extracts the role as "no constraint; informational" and
            scopes every read by ORG, so an admin is already an authorized participant. */}
        <ChatLauncher />
        {/* P2 #3 — and THE PANEL, for the same reason the Messages panel is here:
            `requestCalendarOpen()` is a bus request and the bus has no listener
            unless CalendarLauncher is in the tree. Without this the new row above
            would be a dead control, which is F81b's defect rather than its fix.
            CalendarLauncher renders no floating button (TEACH-UX P1-a retired it)
            and keeps its own `if (!user?.id) return null` gate, so this adds a panel
            and not a FAB. */}
        <CalendarLauncher />
        {/* ⚑ F-11 (C-371) — MIRA, AND THIS IS THE THIRD TIME THIS FILE HAS HAD THIS EXACT BUG.
            Read the two comments directly above: F81 mounted ChatLauncher here because it "was
            mounted only in LayoutShell (the teacher shell)", and P2 #3 mounted CalendarLauncher
            for the same reason. MiraLauncher is the third launcher in that set and the one nobody
            came back for.

            ⚑ SO W47 WAS NOT REGRESSED BY ANY CHANGE TO MIRA. W47 shipped Mira for admins on
            26 Oct and was honestly DONE: the backend admits them (`is_reflective_available`
            excludes only `super_admin`, and `/reflective/opening` has a dedicated admin voice via
            MIRA_ADMIN_OPENING_TURN), and the frontend predicate admits them too. What happened is
            that the admin workspace moved to its OWN chrome, and the launcher stayed behind in the
            shell the two roles used to share. Nine `/admin/*` routes render through AdminShell;
            not one of them had Mira in the tree. That is why the walk found no Mira and why no
            commit between the two dates mentions Mira at all.

            MiraLauncher self-gates completely — `reflectiveAvailable(user)` (role fallback,
            `super_admin` excluded, `reflective_available === true`) plus a silent collapse when
            `/reflective/opening` 404s — so mounting it here adds a surface for an admin whose org
            has Mira and renders null for everyone else. No role check belongs at this call site;
            a second gate here is how the two shells would drift again. */}
        <MiraLauncher />
      </div>
    </div>
  );
}

export default AdminShell;
